import { useEffect, useState } from "react"

export type DraftMessage = {
    message: string | null
    timestamp: number
    hasExistingThread?: boolean
}

type Subscriber = () => void

/*
 * Manages an in-memory cache that is subscribable to support
 * React Hooks. It specifically caches DraftMessages and has
 * some helper functions to support this.
 *
 * There is a basic subscription pattern to notify a caller
 * of cache changes. This is very simple right now and will
 * trigger on any change without specification of what changed.
 * This can be updated as needed.
 */
export class DraftCache {
    private subscribers = new Set<Subscriber>()
    private cache = new Map<string, DraftMessage>()

    /*
     * Add or overwrite a key in the cache.
     * @param {string} otherUsername The key to reference in the cache.
     * @param {DraftMessage} draft The value to put in the cache.
     */
    set(otherUsername: string, draft: DraftMessage): void {
        if (!otherUsername) return
        this.cache.set(otherUsername, draft)
        this.subscribers.forEach((callback) => callback())
    }

    /*
     * Gets the draft from a key in the cache.
     * @param {string} otherUsername The key to reference in the cache.
     * @returns {DraftMessage} The value assoicated to the key or null if not found.
     */
    get(otherUsername: string): DraftMessage | null {
        if (!otherUsername) return null
        return this.cache.get(otherUsername) || null
    }

    /*
     * Gets the draft from a key in the cache.
     * Returns a copy so it's safe if the caller modifies it.
     * @returns {Map<string, DraftMessage> } The entire cache map.
     */
    getAll(): Map<string, DraftMessage> {
        return new Map(this.cache)
    }

    /*
     * Removes an item from the cache based on a key.
     * @param {string} otherUsername The key to reference in the cache.
     */
    clear(otherUsername: string): void {
        if (!otherUsername) return
        this.cache.delete(otherUsername)
        this.subscribers.forEach((callback) => callback())
    }

    /*
     * Removes all the items in the cache.
     */
    clearAll(): void {
        this.cache.clear()
        this.subscribers.forEach((callback) => callback())
    }

    /*
     * Wrapper to help set a ghost draft.
     * @param {string} otherUsername The key to reference in the cache.
     * @param {boolean} hasExistingThread Indicator if this is a ghost
     * conversation (non-existing thread) or ghost message (existing thread)
     * @returns {DraftMessage} The value assoicated to the key or null if not found.
     */
    createGhostDraft(username: string, hasExistingThread?: boolean): void {
        this.set(username, {
            message: null,
            timestamp: Date.now(),
            hasExistingThread,
        })
    }

    /*
     * Wrapper to help clear a ghost draft.
     * @param {string} otherUsername The key to reference in the cache.
     */
    removeGhostDraft(username: string): void {
        this.clear(username)
    }

    /*
     * A caller can add a callback in the event the cache is updated.
     * @param {Subscriber} callback A function to execute when the cache changes.
     */
    subscribe(callback: Subscriber): void {
        this.subscribers.add(callback)
    }

    /*
     * A caller can unsubscribe when they're done.
     * @param {Subscriber} callback The function to unsubscribe.
     */
    unsubscribe(callback: Subscriber): void {
        this.subscribers.delete(callback)
    }
}

/*
 * The global cache.
 */
export const draftCache = new DraftCache()

/*
 * A subscription for a React component to trigger changes on cache changes.
 * In general, the caller will use the default value for the cache to listen
 * to global cache changes.
 * @param {DraftCache} cache The cache that the hook will subscribe to.
 * Unsubscribes when then component is unmounted.
 */
export function useCacheSubscription(cache: DraftCache = draftCache): void {
    const [val, setVal] = useState(true)

    useEffect(() => {
        const callback = () => {
            setVal(!val) // Just need to trigger a change so the caller will rerender.
        }

        cache.subscribe(callback)
        return () => cache.unsubscribe(callback)
    }, [cache])
}
